import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, X, Save } from 'lucide-react';

const EditLeadModal = ({ 
  editingLead, 
  editFormData, 
  onClose, 
  onSave, 
  onFormChange 
}) => {
  if (!editingLead) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <Card className="w-full max-w-2xl my-8">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Edit className="w-5 h-5" />
            Lead Bewerken
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </CardHeader>
        <CardContent className="space-y-4 max-h-[70vh] overflow-y-auto">
          {/* Persoonlijke gegevens */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 border-b pb-2">Persoonlijke gegevens</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-first-name">Voornaam</Label>
                <Input
                  id="edit-first-name"
                  value={editFormData.first_name || ''}
                  onChange={(e) => onFormChange('first_name', e.target.value)}
                  data-testid="edit-first-name"
                />
              </div>
              <div>
                <Label htmlFor="edit-last-name">Achternaam</Label>
                <Input
                  id="edit-last-name"
                  value={editFormData.last_name || ''}
                  onChange={(e) => onFormChange('last_name', e.target.value)}
                  data-testid="edit-last-name"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editFormData.email || ''}
                  onChange={(e) => onFormChange('email', e.target.value)}
                  data-testid="edit-email"
                />
              </div>
              <div>
                <Label htmlFor="edit-phone">Telefoon</Label>
                <Input
                  id="edit-phone"
                  value={editFormData.phone || ''}
                  onChange={(e) => onFormChange('phone', e.target.value)}
                  data-testid="edit-phone"
                />
              </div>
            </div>
          </div>

          {/* Adres gegevens */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 border-b pb-2">Adres</h3>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="edit-address">Straat en huisnummer</Label>
                <Input
                  id="edit-address"
                  value={editFormData.address || ''}
                  onChange={(e) => onFormChange('address', e.target.value)}
                  data-testid="edit-address"
                />
              </div>
              <div>
                <Label htmlFor="edit-postcode">Postcode</Label>
                <Input
                  id="edit-postcode"
                  value={editFormData.postcode || ''}
                  onChange={(e) => onFormChange('postcode', e.target.value)}
                  data-testid="edit-postcode"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-customer-type">Type klant</Label>
              <Select 
                value={editFormData.customer_type || ''} 
                onValueChange={(value) => onFormChange('customer_type', value)}
              >
                <SelectTrigger data-testid="edit-customer-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="particulier">Particulier</SelectItem>
                  <SelectItem value="zakelijk">Zakelijk</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Telecom gegevens */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 border-b pb-2">Telecom</h3>
            <div>
              <Label>Huidige provider(s)</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Telenet', 'Proximus', 'Base', 'Orange', 'Andere'].map(provider => (
                  <Button
                    key={provider}
                    type="button"
                    variant={editFormData.current_providers?.includes(provider) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = editFormData.current_providers || [];
                      if (current.includes(provider)) {
                        onFormChange('current_providers', current.filter(p => p !== provider));
                      } else {
                        onFormChange('current_providers', [...current, provider]);
                      }
                    }}
                  >
                    {provider}
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Gewenste diensten</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {['Internet', 'TV', 'Mobiel', 'Vast telefoon'].map(service => (
                  <Button
                    key={service}
                    type="button"
                    variant={editFormData.services?.includes(service) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = editFormData.services || [];
                      if (current.includes(service)) {
                        onFormChange('services', current.filter(s => s !== service));
                      } else {
                        onFormChange('services', [...current, service]);
                      }
                    }}
                  >
                    {service}
                  </Button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-mobile-count">Aantal mobiel</Label>
                <Input
                  id="edit-mobile-count"
                  type="number"
                  min="0"
                  max="10"
                  value={editFormData.mobile_count || 1}
                  onChange={(e) => onFormChange('mobile_count', parseInt(e.target.value) || 0)}
                  data-testid="edit-mobile-count"
                />
              </div>
              <div>
                <Label htmlFor="edit-tv-count">Aantal TV-decoders</Label>
                <Input
                  id="edit-tv-count"
                  type="number"
                  min="0"
                  max="10"
                  value={editFormData.tv_decoder_count || 1}
                  onChange={(e) => onFormChange('tv_decoder_count', parseInt(e.target.value) || 0)}
                  data-testid="edit-tv-count"
                />
              </div>
            </div>
          </div>

          {/* Energie gegevens */}
          <div className="space-y-3">
            <h3 className="font-medium text-slate-900 border-b pb-2">Energie</h3>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="edit-energy-interest"
                checked={editFormData.energy_interest || false}
                onChange={(e) => onFormChange('energy_interest', e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="edit-energy-interest" className="cursor-pointer">
                Interesse in energie vergelijking
              </Label>
            </div>
            {editFormData.energy_interest && (
              <div>
                <Label htmlFor="edit-energy-provider">Huidige energieleverancier</Label>
                <Select 
                  value={editFormData.current_energy_provider || ''} 
                  onValueChange={(value) => onFormChange('current_energy_provider', value)}
                >
                  <SelectTrigger data-testid="edit-energy-provider">
                    <SelectValue placeholder="Selecteer leverancier" />
                  </SelectTrigger>
                  <SelectContent>
                    {['Engie', 'Luminus', 'Elegant', 'Mega', 'Bolt', 'Octa+', 'Eneco', 'TotalEnergies', 'Essent', 'Andere'].map(provider => (
                      <SelectItem key={provider} value={provider}>{provider}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button onClick={onSave} data-testid="save-lead-edit">
              <Save className="w-4 h-4 mr-2" />
              Opslaan
            </Button>
            <Button variant="outline" onClick={onClose}>
              Annuleren
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EditLeadModal;
